/**
 * Active Intake — resolves the public marketing intake for the landing page.
 *
 * The Intake Command Center (admin) is the authoritative source for intake
 * lifecycle data. This module does NOT keep its own intake records — it calls
 * the server-side public resolver:
 *
 *   GET /api/intakes/public
 *     → { status, intake, courses }
 *
 * The backend resolver applies the existing lifecycle + primary-intake rules:
 *   - intakes accepting registrations (OPEN / CLOSING_SOON) are promoted,
 *     preferring the primary registration intake then the soonest deadline
 *   - otherwise the next UPCOMING intake is announced
 *   - DRAFT / COMPLETED intakes are never returned
 *   - nothing promotable → intake is null → evergreen academy fallback
 *
 * This file only maps the resolved payload onto the display shape the
 * landing components consume — it never decides which intake is public.
 */

import api from '../services/api'
import { fmtDateLong } from '../components/landing/intakeUi'

/**
 * Intake status vocabulary used by the landing components:
 *  - 'upcoming'      : announced, registration not yet open
 *  - 'open'          : registration is live
 *  - 'closing-soon'  : registration live, deadline imminent
 *  - 'closed'        : registration finished, cohort may still be in session
 *  - 'none'          : no active intake — show evergreen academy message
 */
const API_STATUS_MAP = {
  OPEN: 'open',
  CLOSING_SOON: 'closing-soon',
  UPCOMING: 'upcoming',
  CLOSED: 'closed',
}

// ─── Programme imagery ──────────────────────────────────────────────────────
// Courses arrive from the API without artwork. This map only supplies imagery
// per course code — it carries no intake data. Unknown codes use the academy
// hero image.
const PROGRAMME_IMAGES = {
  'PHOTO-101': '/Gallery/Academy/Creative Training Sessions/268A5754.JPG',
  'PHOTO-102': '/Gallery/Academy/Creative Training Sessions/268A5754.JPG',
  'VIDEO-CAM-001': '/Gallery/Academy/Creative Training Sessions/DSC02423.jpg',
  'GRAPH-GD-001': '/Gallery/ai-generated/Logo Design for Local Business.png',
  'AI-TOOLS-001': '/Gallery/Academy/Multimedia Workshops/DSC02588.jpg',
  'ANIM-2D-001': '/Gallery/Academy/Learning In Action/DSC02428 copy.jpg',
  'SOCIAL-DCM-001': '/Gallery/Academy/Class rooms/20250822_163612.jpg',
  'HOL-PHOTO-001': '/Gallery/Academy/Creative Training Sessions/268A5754.JPG',
  'HOL-PHOTO-EDIT-001': '/Gallery/Academy/Creative Training Sessions/268A5754.JPG',
  'HOL-VIDEO-001': '/Gallery/Academy/Creative Training Sessions/DSC02423.jpg',
  'HOL-VIDEO-CC-001': '/Gallery/Academy/Class rooms/20250822_163612.jpg',
  'HOL-VIDEO-EDITING-001': '/Gallery/Academy/Creative Training Sessions/DSC02423.jpg',
  'HOL-GRAPHICS-001': '/Gallery/ai-generated/Logo Design for Local Business.png',
  'HOL-AI-001': '/Gallery/Academy/Multimedia Workshops/DSC02588.jpg',
  'HOL-DM-001': '/Gallery/Academy/Class rooms/20250822_163612.jpg',
}
const DEFAULT_PROGRAMME_IMAGE = '/Gallery/Academy/124414.jpg'
const HERO_IMAGE = '/Gallery/Academy/124414.jpg'

// ─── Evergreen fallback (no active intake) ──────────────────────────────────
export const EVERGREEN_FALLBACK = {
  id: 'evergreen',
  status: 'none',
  hero: {
    eyebrow: 'Synergy Multimedia Academy',
    headline: 'Where Africa’s future creatives are built.',
    description:
      'A futuristic creative institution training filmmakers, photographers, designers, AI creators and digital storytellers for global careers. Explore our full catalogue and apply when the next intake opens.',
    image: HERO_IMAGE,
    primaryCta: { label: 'Explore All Programs', href: '/programs' },
    secondaryCta: { label: 'Talk to Admissions', href: '/advisor' },
  },
  highlights: [],
  programmes: [],
  programsPageHref: '/programs',
  intakePageHref: '/intake',
  enquiryHref: '/advisor',
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** "Holiday Intake 2026" → "Holiday Intake" · "Summer 2027 Intake" → "Summer Intake" */
function shortLabelFromName(name) {
  if (!name) return 'Intake'
  return name.replace(/\b(19|20)\d{2}\b/g, '').replace(/\s{2,}/g, ' ').trim() || name
}

/** "Holiday Intake 2026" → "Holiday 2026" · "January 2027 Intake" → "January 2027" */
function displayNameFromName(name) {
  if (!name) return 'the current'
  return name.replace(/\s+intake\b/i, '').trim() || name
}

function formatFee(fee) {
  const n = Number(fee)
  if (!Number.isFinite(n) || n <= 0) return null
  return `UGX ${n.toLocaleString('en-US')}`
}

function formatDuration(course) {
  if (course.duration) return course.duration
  if (course.duration_weeks) return `${course.duration_weeks} Week${course.duration_weeks === 1 ? '' : 's'}`
  return null
}

/**
 * Resolve the active intake with its programmes attached.
 * Returns null when no public intake exists — the page then renders the
 * evergreen fallback. API failures also resolve to null (never a fake intake).
 */
export async function fetchActiveIntake() {
  const { intake } = await fetchPublicIntakes()
  return intake
}

/**
 * Resolve the full public intake picture in ONE call to the existing public
 * endpoint:
 *
 *   { intake, openIntakes }
 *
 *   intake      — the promoted intake (display record), or null
 *   openIntakes — every intake currently open to applicants, in the order the
 *                 server promoted them. Empty when none are open.
 *
 * The server decides which intakes are public and in what order; this module
 * only maps the payload onto the display shape.
 */
export async function fetchPublicIntakes() {
  try {
    const { data } = await api.get('/intakes/public', { timeout: 5000 })
    if (!data) return { intake: null, openIntakes: [] }
    return {
      intake: data.intake ? buildIntake(data) : null,
      openIntakes: Array.isArray(data.intakes)
        ? data.intakes.map(buildIntakeOption).filter(Boolean)
        : [],
    }
  } catch {
    return { intake: null, openIntakes: [] }
  }
}

/**
 * Map one public intake onto the record the admissions intake chooser renders.
 * Every field comes from the server payload — name, dates, status, program
 * descriptor and the apply destination are never composed locally.
 */
function buildIntakeOption(raw) {
  if (!raw || !raw.id) return null
  const status = API_STATUS_MAP[raw.status] || 'none'
  const name = raw.name || 'Intake'
  return {
    id: String(raw.id),
    name,
    displayName: displayNameFromName(name),
    shortLabel: shortLabelFromName(name),
    code: raw.code || null,
    programType: raw.program_type || 'REGULAR',
    programLabel: raw.program_label || null,
    programSummary: raw.program_summary || null,
    isPrimary: raw.is_primary_registration_intake === true,
    status,
    startDate: raw.start_date || null,
    endDate: raw.end_date || null,
    registrationEnd: raw.registration_close_date || null,
    applyHref: raw.apply_href || `/register?intake_id=${raw.id}`,
    programmesHref: raw.program_type === 'HOLIDAY' ? '/holiday-programs' : '/programs',
  }
}

/**
 * Map the API payload ({ status, intake, courses }) onto the display record
 * the landing components consume.
 */
function buildIntake(data) {
  const raw = data.intake
  const status = API_STATUS_MAP[raw.status] || 'none'
  if (status === 'none' || status === 'closed') return null

  const name = raw.name || 'Current Intake'
  const shortLabel = shortLabelFromName(name)
  const applyHref = raw.apply_href || `/register?intake_id=${raw.id}`

  const courses = Array.isArray(data.courses) ? data.courses : []
  const registered = Number.isFinite(Number(raw.registered_count)) ? Number(raw.registered_count) : null
  const target = Number.isFinite(Number(raw.target_students)) ? Number(raw.target_students) : null
  const seatsAvailable = target !== null && registered !== null
    ? Math.max(0, target - registered)
    : null

  return {
    id: String(raw.id),
    name,
    code: raw.code || null,
    programType: raw.program_type || 'REGULAR',
    isPrimary: raw.is_primary_registration_intake === true,
    shortLabel,
    navLabel: shortLabel,
    status,
    startDate: raw.start_date || null,
    endDate: raw.end_date || null,
    registrationStart: raw.registration_open_date || null,
    registrationEnd: raw.registration_close_date || null,
    applicationDeadline: raw.registration_close_date || null,
    programmeStart: raw.start_date || null,
    programmeEnd: raw.end_date || null,
    format: 'On-site · Kampala',
    seatsAvailable,
    hero: buildHero(raw, status, shortLabel, applyHref),
    highlights: buildHighlights(courses, seatsAvailable),
    programmes: courses.map((c) => buildProgramme(c, raw, status, applyHref)),
    programmesPageHref: '/programs',
    intakePageHref: '/intake',
    enquiryHref: '/advisor',
  }
}

/** Status-aware hero copy generated from the resolved intake's own data. */
function buildHero(intake, status, shortLabel, applyHref) {
  const displayName = displayNameFromName(intake.name)
  const closingSoon = status === 'closing-soon'
  const upcoming = status === 'upcoming'

  let description
  if (upcoming) {
    const opens = intake.registration_open_date ? fmtDateLong(intake.registration_open_date) : null
    description = `Practical, project-led training for aspiring creatives. Applications for the ${displayName} intake ${opens ? `open ${opens}` : 'open soon'} — get ready to secure your seat.`
  } else if (closingSoon) {
    const deadline = intake.registration_close_date ? fmtDateLong(intake.registration_close_date) : null
    description = `Practical, project-led training for aspiring creatives. Applications for the ${displayName} intake ${deadline ? `close ${deadline}` : 'close soon'} — secure your seat and start building real work from day one.`
  } else {
    description = `Practical, project-led training for aspiring creatives. The ${displayName} intake is now open — secure your seat and start building real work from day one.`
  }

  return {
    eyebrow: 'Synergy Multimedia Academy',
    headline: 'Build the creative skills your future needs.',
    description,
    image: HERO_IMAGE,
    primaryCta: upcoming
      ? { label: 'Register Your Interest', href: '/advisor' }
      : { label: `Apply for ${shortLabel}`, href: applyHref },
    secondaryCta: upcoming
      ? { label: 'Explore Programs', href: '/programs' }
      : { label: 'Talk to Admissions', href: '/advisor' },
  }
}

/** Highlights row — generic academy values plus data-derived duration/seats. */
function buildHighlights(courses, seatsAvailable) {
  const weeks = courses.map((c) => Number(c.duration_weeks) || null).filter((w) => w !== null)
  const sharedWeeks = weeks.length > 0 && weeks.every((w) => w === weeks[0]) ? weeks[0] : null
  const highlights = [
    { label: 'Duration', value: sharedWeeks ? `${sharedWeeks} Weeks` : 'Per programme', detail: 'Full-time or part-time' },
    { label: 'Approach', value: 'Real Projects', detail: 'Hands-on studio work' },
    { label: 'Outcome', value: 'Certificate', detail: 'Verifiable' },
    { label: 'Level', value: 'Beginner+', detail: 'No experience needed' },
  ]
  if (seatsAvailable !== null) {
    highlights.push({ label: 'Seats', value: String(seatsAvailable), detail: 'Available this intake' })
  }
  return highlights
}

/**
 * Project an API course into an intake-aware programme card object. Course
 * data comes from the API; imagery comes from the PROGRAMME_IMAGES map.
 */
function buildProgramme(course, intake, status, applyHref) {
  const code = course.course_code || ''
  const acceptsApplications = status === 'open' || status === 'closing-soon'

  return {
    id: `course-${course.id}`,
    code,
    title: course.title || code,
    description: course.description || null,
    image: PROGRAMME_IMAGES[code] || DEFAULT_PROGRAMME_IMAGE,
    duration: formatDuration(course),
    level: course.level || null,
    fee: formatFee(course.fee),
    availability: status,
    seatsAvailable: null,
    startDate: intake.start_date || null,
    endDate: intake.end_date || null,
    registrationOpenDate: intake.registration_open_date || null,
    registrationCloseDate: intake.registration_close_date || null,
    detailHref:
      intake.program_type === 'HOLIDAY'
        ? `/holiday-programs/${encodeURIComponent(code)}`
        : '/programs',
    applyHref: acceptsApplications
      ? `${applyHref}&course=${encodeURIComponent(code)}`
      : null,
    waitlistHref: '/advisor',
  }
}

// ─── Convenience selectors ──────────────────────────────────────────────────

/** Programmes belonging to a given intake. */
export function getIntakeProgrammes(intake) {
  if (!intake || !intake.programmes) return []
  return intake.programmes
}

/** Is the intake currently accepting applications? */
export function isIntakeOpen(intake) {
  return intake && ['open', 'closing-soon'].includes(intake.status)
}

/** Count of programmes actually open for registration in this intake. */
export function countOpenPrograms(intake) {
  if (!intake || !intake.programmes) return 0
  return intake.programmes.filter(
    (p) => p.availability === 'open' || p.availability === 'closing-soon'
  ).length
}

/** Human-readable status label for badges. */
export function statusLabel(status) {
  switch (status) {
    case 'upcoming':
      return 'Upcoming Intake'
    case 'open':
      return 'Now Open'
    case 'closing-soon':
      return 'Closing Soon'
    case 'closed':
      return 'Closed'
    default:
      return ''
  }
}
